import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/use-toast';
import { QuickStartState, DestinationsState } from '@/states';
import { trackEvent } from '@/helpers/external-tools-helper';
import { ACTIVATION_ANALYTICS_EVENTS } from '@/analytics/activation';
import { ClientContainer, UserContainer, ReportsContainer } from '@/providers';
import { ConnectionQuery } from '@/queries/connection';
import { useSession } from '@/states/common';
import { APP_ROUTE } from '@/constants';

export const useActivationCompletion = () => {
  const { t } = useTranslation('activation');
  const navigate = useNavigate();
  const [quickStart, setQuickStart] = useRecoilState(QuickStartState);
  const destination = useRecoilValue(DestinationsState);
  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { state: reportState } = ReportsContainer.useContainer();
  const { data } = useSession();

  const { data: providerAccounts } = ConnectionQuery.useDataSources(
    data.business?.id
  );

  const isActivationFlow = userState.business?.activationFlow === false;
  const isDestinations = Object.keys(destination).length > 0;

  const checkQuickSteps = useCallback(() => {
    const { datasources, destination: quickDestination, report } = quickStart;

    // 1. Check datasource status
    const connectedAccounts =
      providerAccounts?.filter?.((acc: any) => acc?.clientId) ?? [];

    if (connectedAccounts.length === 0 && datasources === 'completed') {
      // Reset to pending if no connections found but marked as completed
      setQuickStart(prevStart => ({
        ...prevStart,
        datasources: 'pending',
      }));
    }

    if (connectedAccounts.length > 0 && datasources === 'pending') {
      setTimeout(() => {
        setQuickStart(prevStart => ({
          ...prevStart,
          datasources: 'completed',
        }));
      }, 3000);
    }

    // 2. Check destination status
    if (!isDestinations && quickDestination === 'completed') {
      setQuickStart(prevState => ({
        ...prevState,
        destination: 'pending',
      }));
    }

    if (isDestinations && quickDestination === 'pending') {
      setQuickStart(prevState => ({
        ...prevState,
        destination: 'completed',
      }));
    }

    // 3. Check report status
    if (reportState.reports.length > 0 && report === 'pending') {
      setQuickStart(prevState => ({
        ...prevState,
        report: 'completed',
      }));
    }

    if (reportState.reports.length === 0 && report === 'completed') {
      setQuickStart(prevState => ({
        ...prevState,
        report: 'pending',
      }));
    }
  }, [
    quickStart,
    providerAccounts,
    isDestinations,
    reportState.reports.length,
    setQuickStart,
  ]);

  // Run automatic detection
  useEffect(() => {
    checkQuickSteps();
  }, [checkQuickSteps]);

  const completeDataSourceStep = () => {
    if (!isActivationFlow) return;

    // Show completion toast first
    const toastRef = toast({
      variant: 'success',
      title: t(
        'step_2_completed',
        'Data sources connected! Moving to Google Drive setup...'
      ),
    });

    // Auto-dismiss toast after 3 seconds
    setTimeout(() => {
      toastRef.dismiss();
    }, 3000);

    // Mark step as completed after showing the message
    setQuickStart(prev => ({ ...prev, datasources: 'completed' }));

    // Track completion
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DATASOURCE_COMPLETED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );

    // Navigate after a delay
    setTimeout(() => {
      if (quickStart.destination !== 'completed') {
        navigate(
          `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DESTINATION}`
        );
      } else if (quickStart.report !== 'completed') {
        // If destination is done but report isn't, go to reports
        navigate(
          `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`
        );
      }
    }, 2000);
  };

  const completeDestinationStep = () => {
    if (!isActivationFlow) return;

    // Show completion toast first
    const toastRef = toast({
      variant: 'success',
      title: t(
        'step_3_completed',
        'Google Drive connected! Moving to report creation...'
      ),
    });

    // Auto-dismiss toast after 3 seconds
    setTimeout(() => {
      toastRef.dismiss();
    }, 3000);

    // Mark step as completed
    setQuickStart(prev => ({ ...prev, destination: 'completed' }));

    // Track completion
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DESTINATION_COMPLETED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );

    // Navigate after a delay
    setTimeout(() => {
      navigate(
        `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`
      );
    }, 2000);
  };

  const completeReportStep = () => {
    if (!isActivationFlow) return;

    // Show completion toast first
    const toastRef = toast({
      variant: 'success',
      title: t(
        'step_4_completed',
        'Report created! Activation flow completed! 🎉'
      ),
    });

    // Auto-dismiss toast after 3 seconds
    setTimeout(() => {
      toastRef.dismiss();
    }, 3000);

    // Mark step as completed
    setQuickStart(prev => ({ ...prev, report: 'completed' }));

    // Track completion
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_CREATE_REPORT_COMPLETED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
  };

  return {
    isActivationFlow,
    completeDataSourceStep,
    completeDestinationStep,
    completeReportStep,
    quickStart,
  };
};
